import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Typography,
  TextField,
  Button,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  CircularProgress,
  IconButton,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import {
  deletePlatformPassengerPartnerGlobalFee,
  listPlatformPassengerPartnerGlobalFees,
  savePlatformPassengerPartnerGlobalFee,
  postPlatformPassengerFeesBackfill,
} from "../../../api/passagensApi";
import { PASSENGER_PARTNER_KEY_OPTIONS, fmtPctDisplay } from "./passengerTaxConstants";

const EMPTY = {
  partner_key: "totalbus",
  taxa_parceiro_pct: 0,
};

export default function PassengerPartnerGlobalFeesPanel({ from, to, dominioId, onProfilesSaved }) {
  const { t } = useTranslation("app");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listPlatformPassengerPartnerGlobalFees();
      setRows(data);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t("platformPage.passengerPartnerFeesLoadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const triggerFeesRecalc = useCallback(async () => {
    if (!from || !to) {
      onProfilesSaved?.();
      return;
    }
    try {
      let offsets = {};
      let tenantOffset = 0;
      for (let i = 0; i < 80; i += 1) {
        const data = await postPlatformPassengerFeesBackfill({
          from,
          to,
          dominioId: dominioId || undefined,
          offsets,
          tenantOffset,
          forceMpBackfill: true,
        });
        offsets = data?.offsets && typeof data.offsets === "object" ? data.offsets : {};
        tenantOffset = Number(data?.tenantOffset ?? tenantOffset);
        if (!data?.hasMore) break;
      }
    } catch {
      /* best effort — perfil já foi salvo */
    }
    onProfilesSaved?.();
  }, [dominioId, from, onProfilesSaved, to]);

  const handleSave = async () => {
    setError("");
    try {
      await savePlatformPassengerPartnerGlobalFee(form);
      setForm(EMPTY);
      await load();
      await triggerFeesRecalc();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t("platformPage.passengerPartnerFeesSaveError"));
    }
  };

  const handleDelete = async (id) => {
    setError("");
    try {
      await deletePlatformPassengerPartnerGlobalFee(id);
      load();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t("platformPage.passengerPartnerFeesSaveError"));
    }
  };

  if (loading) return <CircularProgress size={24} />;

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Alert severity="info" sx={{ py: 0.75 }}>
        {t("platformPage.passengerPartnerFeesHint")}
      </Alert>
      <Typography variant="subtitle2" fontWeight={600}>
        {t("platformPage.passengerPartnerFeesTitle")}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <TextField
          select
          size="small"
          sx={{ minWidth: 200 }}
          label={t("platformPage.passengerTaxColPartner")}
          value={form.partner_key}
          onChange={(e) => setForm({ ...form, partner_key: e.target.value })}
        >
          {PASSENGER_PARTNER_KEY_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="number"
          sx={{ width: 120 }}
          label={t("platformPage.passengerTaxColPartnerPct")}
          value={form.taxa_parceiro_pct}
          onChange={(e) => setForm({ ...form, taxa_parceiro_pct: e.target.value })}
        />
        <Button variant="contained" onClick={handleSave}>
          {t("common.save", { defaultValue: "Salvar" })}
        </Button>
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("platformPage.passengerTaxColPartner")}</TableCell>
              <TableCell align="right">{t("platformPage.passengerTaxColPartnerPct")}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary">
                    {t("platformPage.passengerPartnerFeesEmpty")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    {PASSENGER_PARTNER_KEY_OPTIONS.find((o) => o.value === r.partner_key)?.label || r.partner_key}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {fmtPctDisplay(r.taxa_parceiro_pct)}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleDelete(r.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
